import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExecutionStatus, isTerminalStatus, ResultDisplayConfig } from '../types';

import styles from './ResultDisplay.module.scss';

interface ResultDisplayProps {
  status: ExecutionStatus;
  output: unknown | null;
  error: string | null;
  durationMs: number | null;
  completedAt: string | null;
  resultDisplay: ResultDisplayConfig | null;
  onDismissError?: () => void;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString();
}

export const StatusBadge: React.FC<{ status: ExecutionStatus }> = ({ status }) => {
  const { t } = useTranslation();
  // Statuses the backend may add later have neither a modifier class nor a
  // translation key, so fall back to the neutral badge and the raw value.
  const className = `${styles.badge} ${styles[`badge_${status}`] || ''}`;
  return (
    <span className={className}>
      {t(`status.${status}`, { defaultValue: status })}
    </span>
  );
};

const TableDisplay: React.FC<{ data: Record<string, unknown>[] }> = ({ data }) => {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const columns = useMemo(() => {
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  }, [data]);

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const rawA = a[sortKey];
      const rawB = b[sortKey];

      // Push null/undefined to the end regardless of sort direction.
      const aNullish = rawA === null || rawA === undefined;
      const bNullish = rawB === null || rawB === undefined;
      if (aNullish && bNullish) return 0;
      if (aNullish) return 1;
      if (bNullish) return -1;

      const numA =
        typeof rawA === 'number'
          ? rawA
          : typeof rawA === 'string' && Number.isFinite(parseFloat(rawA))
            ? parseFloat(rawA)
            : NaN;
      const numB =
        typeof rawB === 'number'
          ? rawB
          : typeof rawB === 'string' && Number.isFinite(parseFloat(rawB))
            ? parseFloat(rawB)
            : NaN;

      if (Number.isFinite(numA) && Number.isFinite(numB)) {
        return sortAsc ? numA - numB : numB - numA;
      }

      const va = String(rawA);
      const vb = String(rawB);
      return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [data, sortKey, sortAsc]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                onClick={() => handleSort(col)}
                className={styles.sortableHeader}
              >
                {col}
                {sortKey === col && (
                  <span className={styles.sortArrow}>
                    {sortAsc ? ' \u25B2' : ' \u25BC'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col}>{String(row[col] ?? '')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const JsonDisplay: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  return (
    <div className={styles.jsonWrapper}>
      <button className={styles.copyButton} onClick={handleCopy} type="button">
        {copied ? t('Copied!') : t('Copy')}
      </button>
      <pre className={styles.jsonPre}>{json}</pre>
    </div>
  );
};

export const ResultDisplay: React.FC<ResultDisplayProps> = ({
  status,
  output,
  error,
  durationMs,
  completedAt,
  resultDisplay,
  onDismissError,
}) => {
  const { t } = useTranslation();

  if (status === 'failed' || status === 'timeout') {
    return (
      <div className={styles.resultContainer}>
        <div className={styles.errorCard}>
          <div className={styles.errorHeader}>
            <StatusBadge status={status} />
            {onDismissError && (
              <button
                className={styles.dismissButton}
                onClick={onDismissError}
                type="button"
              >
                {t('Dismiss')}
              </button>
            )}
          </div>
          <p className={styles.errorMessage}>
            {error || t('An error occurred')}
          </p>
        </div>
      </div>
    );
  }

  // Terminal, but neither a failure nor a success carrying output — a run the
  // user cancelled, or a status this build predates. Show the status on its
  // own instead of an empty panel.
  if (status !== 'completed' && isTerminalStatus(status)) {
    return (
      <div className={styles.resultContainer}>
        <div className={styles.resultHeader}>
          <StatusBadge status={status} />
        </div>
      </div>
    );
  }

  if (status !== 'completed' || output === null || output === undefined) {
    return null;
  }

  const displayType = resultDisplay?.type;
  const isTableData =
    displayType === 'table' &&
    Array.isArray(output) &&
    output.length > 0 &&
    output[0] !== null &&
    typeof output[0] === 'object';

  return (
    <div className={styles.resultContainer}>
      <div className={styles.resultHeader}>
        <StatusBadge status={status} />
      </div>

      <div className={styles.resultBody}>
        {isTableData ? (
          <TableDisplay data={output as Record<string, unknown>[]} />
        ) : displayType === 'text' ? (
          <pre className={styles.textPre}>{String(output)}</pre>
        ) : (
          <JsonDisplay data={output} />
        )}
      </div>

      <div className={styles.resultFooter}>
        {durationMs !== null && (
          <span className={styles.footerItem}>
            {t('Duration')}: {formatDuration(durationMs)}
          </span>
        )}
        {completedAt && (
          <span className={styles.footerItem}>
            {t('Completed')}: {formatTimestamp(completedAt)}
          </span>
        )}
      </div>
    </div>
  );
};
